import { envVarUtil, getTelemetrySettings, telemetryBuildTimeEnvVars, urlUtil } from '@cmg/common';

const {
  getOrThrow,
  parseBoolOrThrow,
  parseFloatOrThrow,
  getLocalStorageEnvOverrides,
  getSessionEnvOverrides,
} = envVarUtil;

// Exported for testing purposes
export const buildTimeEnvVars = {
  ...telemetryBuildTimeEnvVars,
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_CLUSTER_ENVIRONMENT: process.env.REACT_APP_CLUSTER_ENVIRONMENT,
  REACT_APP_ROOT_DOMAIN: process.env.REACT_APP_ROOT_DOMAIN,
  REACT_APP_MIXPANEL_TOKEN: process.env.REACT_APP_MIXPANEL_TOKEN,
  REACT_APP_AG_GRID_LICENSE_KEY: process.env.REACT_APP_AG_GRID_LICENSE_KEY,
  REACT_APP_MUI_X_PRO_LICENSE_KEY: process.env.REACT_APP_MUI_X_PRO_LICENSE_KEY,
  REACT_APP_DATADOG_APPLICATION_ID: process.env.REACT_APP_DATADOG_APPLICATION_ID,
  REACT_APP_DATADOG_CLIENT_TOKEN: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
  REACT_APP_DATADOG_TRACING_ORIGIN: process.env.REACT_APP_DATADOG_TRACING_ORIGIN,
  REACT_APP_CLIENT_APP_VERSION: process.env.REACT_APP_CLIENT_APP_VERSION,
  REACT_APP_BUILD_CLIENT_APP_VERSION: process.env.REACT_APP_BUILD_CLIENT_APP_VERSION,
  REACT_APP_OIDC_AUTHORITY_BASE_URL: process.env.REACT_APP_OIDC_AUTHORITY_BASE_URL,
  REACT_APP_AUTH_TOKEN_RENEWAL_INTERVAL_IN_MINUTES:
    process.env.REACT_APP_AUTH_TOKEN_RENEWAL_INTERVAL_IN_MINUTES,
  REACT_APP_IDENTITY_SPA_BASE_URL: process.env.REACT_APP_IDENTITY_SPA_BASE_URL,
  REACT_APP_IDENTITY_SERVICE_BASE_URL: process.env.REACT_APP_IDENTITY_SERVICE_BASE_URL,
  REACT_APP_HUBSPOT_SUPPORT_FORM_PORTAL_ID: process.env.REACT_APP_HUBSPOT_SUPPORT_FORM_PORTAL_ID,
  REACT_APP_HUBSPOT_SUPPORT_FORM_ID: process.env.REACT_APP_HUBSPOT_SUPPORT_FORM_ID,
  REACT_APP_XC_SPA_BASE_URL: process.env.REACT_APP_XC_SPA_BASE_URL,
  REACT_APP_PUBSUB_COMMUNICATION: process.env.REACT_APP_PUBSUB_COMMUNICATION,
  REACT_APP_SIGNAL_LOG_LEVEL: process.env.REACT_APP_SIGNAL_LOG_LEVEL,
  REACT_APP_DLGW_GRAPHQL_API_URL: process.env.REACT_APP_DLGW_GRAPHQL_API_URL,
  REACT_APP__ENABLE_FEATURE__NEW_DIRECT_OFFERING_FIELDS_ECM_38278:
    process.env.REACT_APP__ENABLE_FEATURE__NEW_DIRECT_OFFERING_FIELDS_ECM_38278,
  REACT_APP__ENABLE_FEATURE__COMPANY_SEARCH_V2:
    process.env.REACT_APP__ENABLE_FEATURE__COMPANY_SEARCH_V2,
  REACT_APP__ENABLE_FEATURE__ESTIMATES_ECONOMICS_SUPPORT_MODEL:
    process.env.REACT_APP__ENABLE_FEATURE__ESTIMATES_ECONOMICS_SUPPORT_MODEL,
  REACT_APP__ENABLE_FEATURE__MDL_REPORTS_ECONOMICS_ESTIMATES:
    process.env.REACT_APP__ENABLE_FEATURE__MDL_REPORTS_ECONOMICS_ESTIMATES,
  REACT_APP__ENABLE_FEATURE__DL_UW_REPORTS_ECONOMICS:
    process.env.REACT_APP__ENABLE_FEATURE__DL_UW_REPORTS_ECONOMICS,
  REACT_APP__ENABLE_FEATURE__ETF_BENCHMARK: process.env.REACT_APP__ENABLE_FEATURE__ETF_BENCHMARK,
  REACT_APP__ENABLE_FEATURE__LOCALIZATION: process.env.REACT_APP__ENABLE_FEATURE__LOCALIZATION,
  REACT_APP__ENABLE_FEATURE__PRE_IPO_DATA: process.env.REACT_APP__ENABLE_FEATURE__PRE_IPO_DATA,
  REACT_APP__ENABLE_FEATURE__PIPE_DATA: process.env.REACT_APP__ENABLE_FEATURE__PIPE_DATA,
  REACT_APP__ENABLE_FEATURE__INTERNATIONAL_CONVERTS:
    process.env.REACT_APP__ENABLE_FEATURE__INTERNATIONAL_CONVERTS,
  REACT_APP__ENABLE_FEATURE__DATAGRID_USAGE_LOGGING:
    process.env.REACT_APP__ENABLE_FEATURE__DATAGRID_USAGE_LOGGING,
  REACT_APP__ENABLE_FEATURE__ATM_REPORT_NEXT:
    process.env.REACT_APP__ENABLE_FEATURE__ATM_REPORT_NEXT,
  REACT_APP__ENABLE_FEATURE__IPO_FUNDAMENTALS:
    process.env.REACT_APP__ENABLE_FEATURE__IPO_FUNDAMENTALS,
  REACT_APP__ENABLE_FEATURE__MDL_REPORTS_REVERSE_INQUIRIES:
    process.env.REACT_APP__ENABLE_FEATURE__MDL_REPORTS_REVERSE_INQUIRIES,
  REACT_APP__ENABLE_FEATURE__GLOBAL_ECM_CONVERTS_REPORT:
    process.env.REACT_APP__ENABLE_FEATURE__GLOBAL_ECM_CONVERTS_REPORT,
};

// Merge runtime variables provided by runtime-env.js which is generated in the docker entrypoint.
// Runtime variables take precedence.
const mergedVars = {
  ...buildTimeEnvVars,
  ...globalThis.env,
  ...getLocalStorageEnvOverrides(),
  ...getSessionEnvOverrides(),
};

const appSettings = {
  env: {
    clusterEnvironment: getOrThrow(mergedVars.REACT_APP_CLUSTER_ENVIRONMENT),
    rootDomain: getOrThrow(mergedVars.REACT_APP_ROOT_DOMAIN),
    secureCookies: !urlUtil.isLocalhost(getOrThrow(mergedVars.REACT_APP_ROOT_DOMAIN)),
    // refers to local development. all other environments run a 'production' version.
    isDevelopment: mergedVars.NODE_ENV === 'development',
    enableDataGridValidations: getOrThrow(mergedVars.REACT_APP_CLUSTER_ENVIRONMENT) === 'local',
    identitySpaBaseUrl: getOrThrow(mergedVars.REACT_APP_IDENTITY_SPA_BASE_URL),
    identityServiceBaseUrl: getOrThrow(mergedVars.REACT_APP_IDENTITY_SERVICE_BASE_URL),
    hubspotSupportFormPortalId: mergedVars.REACT_APP_HUBSPOT_SUPPORT_FORM_PORTAL_ID,
    hubspotSupportFormId: mergedVars.REACT_APP_HUBSPOT_SUPPORT_FORM_ID,
    xcSpaBaseUrl: mergedVars.REACT_APP_XC_SPA_BASE_URL,
    dlgwGraphqlApiUrl: getOrThrow(mergedVars.REACT_APP_DLGW_GRAPHQL_API_URL, '/dlgw/graphql/'),
  },
  features: {
    isNewDirectOfferingFieldsOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__NEW_DIRECT_OFFERING_FIELDS_ECM_38278,
      false
    ),
    isGlobalEcmV2InDLOn: false,
    isCompanySearchV2On: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__COMPANY_SEARCH_V2,
      true
    ),
    isEstimatesEconomicsSupportModelOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__ESTIMATES_ECONOMICS_SUPPORT_MODEL,
      false
    ),
    isMdlReportsEconomicsEstimatesOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__MDL_REPORTS_ECONOMICS_ESTIMATES,
      false
    ),
    isDlUwReportsEconomicsOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__DL_UW_REPORTS_ECONOMICS,
      false
    ),
    isEtfBenchmarkOn: parseBoolOrThrow(mergedVars.REACT_APP__ENABLE_FEATURE__ETF_BENCHMARK, false),
    isLocalizationOn: parseBoolOrThrow(mergedVars.REACT_APP__ENABLE_FEATURE__LOCALIZATION, false),
    isPreIpoDataOn: parseBoolOrThrow(mergedVars.REACT_APP__ENABLE_FEATURE__PRE_IPO_DATA, false),
    isPipeDataOn: parseBoolOrThrow(mergedVars.REACT_APP__ENABLE_FEATURE__PIPE_DATA, false),
    isIntlConvertsOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__INTERNATIONAL_CONVERTS,
      false
    ),
    isDataGridUsageLoggingOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__DATAGRID_USAGE_LOGGING,
      false
    ),
    isAtmReportNextOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__ATM_REPORT_NEXT,
      false
    ),
    isIpoFundamentalsOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__IPO_FUNDAMENTALS,
      false
    ),
    isReverseInquiriesReportOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__MDL_REPORTS_REVERSE_INQUIRIES,
      false
    ),
    isGlobalEcmConvertsReportOn: parseBoolOrThrow(
      mergedVars.REACT_APP__ENABLE_FEATURE__GLOBAL_ECM_CONVERTS_REPORT,
      false
    ),
  },
  licenseKeys: {
    agGridLicenseKey: getOrThrow(mergedVars.REACT_APP_AG_GRID_LICENSE_KEY, 'DUMMY_KEY'),
    muiXProLicenseKey: getOrThrow(mergedVars.REACT_APP_MUI_X_PRO_LICENSE_KEY, 'DUMMY_KEY'),
  },
  client: {
    appVersion: getOrThrow(mergedVars.REACT_APP_CLIENT_APP_VERSION),
    appVersionBuild: getOrThrow(mergedVars.REACT_APP_BUILD_CLIENT_APP_VERSION),
    basename: '/',
  },
  logging: {
    mixPanelToken: getOrThrow(mergedVars.REACT_APP_MIXPANEL_TOKEN, 'mock-token'),
    // https://docs.datadoghq.com/real_user_monitoring/browser/#initialization-parameters - 'applicationId'
    datadogApplicationId: mergedVars.REACT_APP_DATADOG_APPLICATION_ID,
    // https://docs.datadoghq.com/real_user_monitoring/browser/#initialization-parameters - 'clientToken'
    datadogClientToken: mergedVars.REACT_APP_DATADOG_CLIENT_TOKEN,
    // https://docs.datadoghq.com/real_user_monitoring/browser/#initialization-parameters - 'allowedTracingOrigins' - single item of array
    datadogTracingOrigin: new RegExp(getOrThrow(mergedVars.REACT_APP_DATADOG_TRACING_ORIGIN)),
    // https://docs.datadoghq.com/real_user_monitoring/browser/#initialization-parameters - 'service'
    datadogServiceName: 'ecm-client-web',
  },
  signalR: {
    signalRLogLevel: parseFloatOrThrow(mergedVars.REACT_APP_SIGNAL_LOG_LEVEL, 2),
  },
  auth: {
    /**
     * TODO: remove the default value when the env var is set in all environments.
     */
    oidcAuthorityBaseUrl: getOrThrow(mergedVars.REACT_APP_OIDC_AUTHORITY_BASE_URL),
    tokenRenewalIntervalInMinutes: parseFloatOrThrow(
      mergedVars.REACT_APP_AUTH_TOKEN_RENEWAL_INTERVAL_IN_MINUTES,
      30
    ),
    clientId: 'cmg-app',
    scopes: {
      dl: 'dl:full',
      mgmt_api: 'mgmt_api:full',
      xc: 'xc:full',
    },
  },
  notifications: {
    pollingIntervalInMin: 1,
  },
  telemetry: getTelemetrySettings(mergedVars),
};

export type AppSettings = typeof appSettings;

export function getAppSettings(): Readonly<AppSettings> {
  return appSettings;
}

export function getEnvVars() {
  return appSettings.env;
}

export { appSettings };

export function getFeatureToggles() {
  return getAppSettings().features;
}
